<template>
  <main class="guests-page">
    <div class="guests-page__head">
      <div>
        <h1 class="guests-page__title">Magazzino forniture</h1>
        <p class="guests-page__subtitle">
          Stock forniture personali (panni/presidi) e archivio bolle di
          consegna.
        </p>
      </div>
      <router-link to="/home" class="villa-btn villa-btn--ghost">
        <i class="fa-solid fa-arrow-left"></i> Dashboard
      </router-link>
    </div>

    <div class="facility-tabs">
      <button
        v-for="t in tabs"
        :key="t.key"
        class="facility-tab"
        :class="{ 'facility-tab--active': activeTab === t.key }"
        @click="activeTab = t.key"
      >
        <i :class="t.icon"></i>
        <span>{{ t.label }}</span>
      </button>
    </div>

    <!-- ============ STOCK ============ -->
    <section v-show="activeTab === 'stock'">
      <div class="guest-detail" style="margin-bottom: 14px">
        <div class="supply-filters">
          <select v-model="facilityId" class="search-box" @change="loadStock">
            <option value="">Tutte le strutture</option>
            <option v-for="f in facilities" :key="f.id" :value="f.id">
              {{ f.name }}
            </option>
          </select>
          <label class="billing-skip">
            <input v-model="lowStockOnly" type="checkbox" @change="loadStock" />
            Solo sotto soglia
          </label>
          <button
            class="villa-btn villa-btn--ghost"
            :disabled="loadingStock"
            @click="loadStock"
          >
            <i
              :class="
                loadingStock
                  ? 'fa-solid fa-spinner fa-spin'
                  : 'fa-solid fa-rotate'
              "
            ></i>
            Aggiorna
          </button>
        </div>
      </div>

      <div v-if="loadingStock" class="guests-loading">Caricamento…</div>
      <div v-else-if="!rows.length" class="guests-loading">
        Nessuna fornitura trovata.
      </div>
      <div v-else class="guests-table-wrap">
        <table class="guests-table">
          <thead>
            <tr>
              <th>Ospite</th>
              <th>Fornitura</th>
              <th>Categoria</th>
              <th>Stock</th>
              <th>Soglia</th>
              <th>Ultimo carico</th>
              <th>Ultimo scarico</th>
              <th class="actions-cell">Cartella</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.supplyItemId">
              <td>{{ r.guestName || "—" }}</td>
              <td>{{ r.name }}</td>
              <td>{{ catLabel(r.category) }}</td>
              <td>
                <span :class="{ 'supply-low': r.lowStock }">
                  {{ r.currentStock }} {{ r.unit }}
                </span>
                <span
                  v-if="r.lowStock"
                  class="status-badge status-badge--suspended"
                  style="margin-left: 6px"
                >
                  bassa
                </span>
              </td>
              <td>{{ r.minThreshold != null ? r.minThreshold : "—" }}</td>
              <td>{{ fmtDate(r.lastLoadAt) }}</td>
              <td>{{ fmtDate(r.lastUnloadAt) }}</td>
              <td class="actions-cell">
                <router-link
                  class="icon-btn"
                  :to="`/guests/${r.guestId}`"
                  title="Apri cartella ospite"
                >
                  <i class="fa-solid fa-arrow-up-right-from-square"></i>
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- ============ DOCUMENTI BOLLE ============ -->
    <section v-show="activeTab === 'docs'">
      <div class="guest-detail" style="margin-bottom: 14px">
        <div class="supply-filters">
          <select v-model="docFilters.facilityId" class="search-box">
            <option value="">Tutte le strutture</option>
            <option v-for="f in facilities" :key="f.id" :value="f.id">
              {{ f.name }}
            </option>
          </select>
          <select v-model="docFilters.category" class="search-box">
            <option value="">Tutti i presidi</option>
            <option value="absorbent_pad">Panno</option>
            <option value="bed_pad">Traversa</option>
            <option value="other">Altro</option>
          </select>
          <select v-model="docFilters.supplierType" class="search-box">
            <option value="">Tutte le provenienze</option>
            <option value="public_entity">Ente pubblico</option>
            <option value="affiliated_provider">Fornitore convenzionato</option>
            <option value="family">Famiglia</option>
            <option value="private">Privato</option>
            <option value="other">Altro</option>
          </select>
          <input
            v-model="docFilters.from"
            type="date"
            class="search-box"
            title="Dal"
          />
          <input
            v-model="docFilters.to"
            type="date"
            class="search-box"
            title="Al"
          />
          <button
            class="villa-btn villa-btn--ghost"
            :disabled="loadingDocs"
            @click="loadDocs"
          >
            <i
              :class="
                loadingDocs
                  ? 'fa-solid fa-spinner fa-spin'
                  : 'fa-solid fa-rotate'
              "
            ></i>
            Aggiorna
          </button>
        </div>
      </div>

      <div v-if="loadingDocs" class="guests-loading">Caricamento…</div>
      <div v-else-if="!docRows.length" class="guests-loading">
        Nessuna bolla trovata.
      </div>
      <div v-else class="guests-table-wrap">
        <table class="guests-table">
          <thead>
            <tr>
              <th>N° DDT</th>
              <th>Data</th>
              <th>Ospite</th>
              <th>Fornitore</th>
              <th>Presidi</th>
              <th>Righe</th>
              <th class="actions-cell">Azioni</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in docRows" :key="d.id">
              <td>
                {{ d.ddtNumber || "—" }}
                <span
                  v-if="!d.hasFile"
                  class="status-badge status-badge--suspended"
                  style="margin-left: 6px"
                >
                  {{ d.guestDocumentId ? "PDF archiviato" : "PDF assente" }}
                </span>
              </td>
              <td>{{ fmtDate(d.ddtDate) }}</td>
              <td>{{ d.guestName || "—" }}</td>
              <td>{{ d.supplierName || "—" }}</td>
              <td>
                {{ (d.categories || []).map(catLabel).join(", ") || "—" }}
              </td>
              <td>{{ d.linesCount }}</td>
              <td class="actions-cell">
                <button
                  class="icon-btn"
                  title="Visualizza bolla"
                  :disabled="!d.hasFile || docBusy.id === d.id"
                  @click="viewDoc(d)"
                >
                  <i
                    :class="
                      docBusy.id === d.id && docBusy.action === 'view'
                        ? 'fa-solid fa-spinner fa-spin'
                        : 'fa-solid fa-eye'
                    "
                  ></i>
                </button>
                <button
                  class="icon-btn"
                  title="Stampa bolla"
                  :disabled="!d.hasFile || docBusy.id === d.id"
                  @click="printDoc(d)"
                >
                  <i
                    :class="
                      docBusy.id === d.id && docBusy.action === 'print'
                        ? 'fa-solid fa-spinner fa-spin'
                        : 'fa-solid fa-print'
                    "
                  ></i>
                </button>
                <button
                  class="icon-btn"
                  title="Scarica bolla"
                  :disabled="!d.hasFile || docBusy.id === d.id"
                  @click="downloadDoc(d)"
                >
                  <i
                    :class="
                      docBusy.id === d.id && docBusy.action === 'download'
                        ? 'fa-solid fa-spinner fa-spin'
                        : 'fa-solid fa-download'
                    "
                  ></i>
                </button>
                <button
                  v-if="canArchiveDdtPdf"
                  class="icon-btn icon-btn--danger"
                  title="Archivia PDF bolla"
                  :disabled="!d.hasFile || docBusy.id === d.id"
                  @click="askArchiveDoc(d)"
                >
                  <i class="fa-solid fa-box-archive"></i>
                </button>
                <router-link
                  class="icon-btn"
                  :to="`/guests/${d.guestId}`"
                  title="Apri cartella ospite"
                >
                  <i class="fa-solid fa-arrow-up-right-from-square"></i>
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Conferma archiviazione PDF bolla (soft delete del solo GuestDocument).
         Bolla, righe e movimenti di stock restano invariati. -->
    <div
      v-if="archiveModal.open"
      class="villa-modal-backdrop"
      @click.self="archiveModal.open = false"
    >
      <div class="villa-confirm" style="max-width: 440px">
        <h3 class="villa-confirm__title">Archivia PDF bolla</h3>
        <p class="villa-confirm__text">
          Vuoi archiviare il PDF della bolla
          <strong>{{ archiveModal.label }}</strong
          >? La bolla, le righe e i movimenti di stock restano invariati: viene
          archiviato solo il file PDF, che non sarà più visualizzabile o
          scaricabile.
        </p>
        <div class="villa-confirm__actions">
          <button
            class="villa-btn villa-btn--ghost"
            :disabled="archiveModal.busy"
            @click="archiveModal.open = false"
          >
            Annulla
          </button>
          <button
            class="villa-btn villa-btn--danger"
            :disabled="archiveModal.busy"
            @click="confirmArchiveDoc"
          >
            {{ archiveModal.busy ? "Archiviazione…" : "Archivia PDF" }}
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  printPdfFromResponse,
  openPdfFromResponse,
  isPopupBlocked,
  POPUP_BLOCKED_MESSAGE,
} from "@/utils/printPdf";

const CAT = { absorbent_pad: "Panno", bed_pad: "Traversa", other: "Altro" };

export default {
  name: "SuppliesOverviewPage",
  data() {
    return {
      activeTab: "stock",
      tabs: [
        { key: "stock", label: "Stock", icon: "fa-solid fa-box-open" },
        {
          key: "docs",
          label: "Documenti bolle",
          icon: "fa-solid fa-file-lines",
        },
      ],
      facilities: [],
      facilityId: "",
      lowStockOnly: false,
      rows: [],
      loadingStock: true,
      docFilters: {
        facilityId: "",
        category: "",
        supplierType: "",
        from: "",
        to: "",
      },
      docRows: [],
      loadingDocs: false,
      // Stato azioni PDF bolla, per-riga (id bolla + azione in corso).
      docBusy: { id: null, action: null },
      archiveModal: {
        open: false,
        busy: false,
        docId: null,
        documentId: null,
        guestId: null,
        label: "",
      },
      docsLoadedOnce: false,
    };
  },
  computed: {
    ...mapGetters(["canArchiveDdtPdf"]),
  },
  watch: {
    activeTab(tab) {
      if (tab === "docs" && !this.docsLoadedOnce) this.loadDocs();
    },
  },
  methods: {
    ...mapActions([
      "fetchFacilities",
      "fetchSuppliesOverview",
      "fetchDeliveryNotesOverview",
      "downloadGuestDocument",
      "archiveGuestDocument",
    ]),
    catLabel: (c) => CAT[c] || c,
    fmtDate(d) {
      if (!d) return "—";
      const s = String(d).slice(0, 10).split("-");
      return s.length === 3 ? `${s[2]}/${s[1]}/${s[0]}` : d;
    },
    async loadStock() {
      this.loadingStock = true;
      try {
        this.rows = await this.fetchSuppliesOverview({
          facilityId: this.facilityId || undefined,
          lowStockOnly: this.lowStockOnly,
        });
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Caricamento non riuscito."
        );
      } finally {
        this.loadingStock = false;
      }
    },
    async loadDocs() {
      this.loadingDocs = true;
      this.docsLoadedOnce = true;
      try {
        const params = {};
        Object.entries(this.docFilters).forEach(([k, v]) => {
          if (v) params[k] = v;
        });
        this.docRows = await this.fetchDeliveryNotesOverview(params);
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Caricamento bolle non riuscito."
        );
      } finally {
        this.loadingDocs = false;
      }
    },
    async viewDoc(d) {
      if (!d.hasFile || !d.guestDocumentId) return;
      this.docBusy = { id: d.id, action: "view" };
      try {
        const res = await this.downloadGuestDocument({
          guestId: d.guestId,
          documentId: d.guestDocumentId,
        });
        openPdfFromResponse(res);
      } catch (err) {
        this.$toast.error(
          isPopupBlocked(err)
            ? POPUP_BLOCKED_MESSAGE
            : err?.response?.data?.message ||
                "PDF non disponibile o archiviato."
        );
      } finally {
        this.docBusy = { id: null, action: null };
      }
    },
    async printDoc(d) {
      if (!d.hasFile || !d.guestDocumentId) return;
      this.docBusy = { id: d.id, action: "print" };
      try {
        const res = await this.downloadGuestDocument({
          guestId: d.guestId,
          documentId: d.guestDocumentId,
        });
        printPdfFromResponse(res);
      } catch (err) {
        this.$toast.error(
          isPopupBlocked(err)
            ? POPUP_BLOCKED_MESSAGE
            : err?.response?.data?.message || "Stampa non riuscita."
        );
      } finally {
        this.docBusy = { id: null, action: null };
      }
    },
    async downloadDoc(d) {
      if (!d.hasFile || !d.guestDocumentId) return;
      this.docBusy = { id: d.id, action: "download" };
      let url = null;
      try {
        const res = await this.downloadGuestDocument({
          guestId: d.guestId,
          documentId: d.guestDocumentId,
        });
        const blob = new Blob([res.data], {
          type: res.data?.type || "application/pdf",
        });
        url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = d.documentName || `bolla-${d.ddtNumber || d.id}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Download non riuscito."
        );
      } finally {
        if (url) window.URL.revokeObjectURL(url);
        this.docBusy = { id: null, action: null };
      }
    },
    askArchiveDoc(d) {
      if (!d.hasFile) return;
      this.archiveModal = {
        open: true,
        busy: false,
        docId: d.id,
        documentId: d.guestDocumentId,
        guestId: d.guestId,
        label: d.ddtNumber || this.fmtDate(d.ddtDate) || "bolla",
      };
    },
    async confirmArchiveDoc() {
      this.archiveModal.busy = true;
      try {
        await this.archiveGuestDocument({
          guestId: this.archiveModal.guestId,
          documentId: this.archiveModal.documentId,
        });
        this.$toast.success("PDF della bolla archiviato.");
        this.archiveModal.open = false;
        await this.loadDocs();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Archiviazione non riuscita."
        );
      } finally {
        this.archiveModal.busy = false;
      }
    },
  },
  async mounted() {
    try {
      this.facilities = await this.fetchFacilities();
    } catch {
      this.facilities = [];
    }
    await this.loadStock();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style src="@/css/AppLayout.css" />
<style scoped>
.supply-filters {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}
.supply-low {
  color: #b91c1c;
  font-weight: 700;
}
.villa-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 43, 87, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}
</style>
